import { useContext, useEffect, useMemo, useState } from 'react'
import Papa from 'papaparse'
import { getAuth } from 'firebase/auth'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import CsvImportPreviewItem from './CsvImportPreviewItem'
import { PREVIEWED_FIELDS_DEFAULT, PreviewField } from '../../services/CsvImportService'
import ApiContext from '../../context/ApiContext'
import AuthContext from '../../context/AuthContext'
import Loader from '../Loader/Loader'

type Props = {
	dialogOpen: boolean
	setDialogOpen: (open: boolean) => void
	csvFile: File
}

const CsvImportPreview = ({ dialogOpen, setDialogOpen, csvFile }: Props) => {
	const auth = getAuth()
	const apiContext = useContext(ApiContext)
	const authContext = useContext(AuthContext)
	const queryClient = useQueryClient()
	const [previewFields, setPreviewFields] = useState<ReadonlyArray<PreviewField>>(PREVIEWED_FIELDS_DEFAULT)
	const [csvHasHeader, setCsvHasHeader] = useState(true)
	const [csvFields, setCsvFields] = useState<string[][]>([])
	const [tempHeader, setTempHeader] = useState<string[]>([])

	const requiredFieldsFilled = useMemo(
		() => previewFields.filter(field => field.required).every(field => field.columnIndex !== undefined),
		[previewFields],
	)

	useEffect(() => {
		if (!dialogOpen) {
			setPreviewFields(PREVIEWED_FIELDS_DEFAULT)
			setCsvHasHeader(true)
			setTempHeader([])
		}
	}, [dialogOpen])
	useEffect(() => {
		Papa.parse<string[]>(csvFile, {
			skipEmptyLines: 'greedy',
			complete: results => {
				if (results.errors.length > 0) {
					toast.error('Error parsing csv', {
						description: `There are ${results.errors.length} errors in the csv file`,
					})
					setDialogOpen(false)
					return
				}
				const fixedFirstRow = results.data[0].map((field, index) =>
					field === '' ? `UnnamedField_${index}` : field,
				)
				setTempHeader(fixedFirstRow)
				setCsvFields(results.data)
			},
		})
	}, [csvFile, setDialogOpen])

	const importContactFields = async () => {
		const idToken = await auth.currentUser?.getIdToken(true)
		try {
			const response = await fetch(`${apiContext.apiUrl}/companies/${authContext.company?.id}/csv`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${idToken}`,
				},
				body: JSON.stringify({
					hasHeader: csvHasHeader,
					csv: Papa.unparse(csvFields),
					tagIndexObject: previewFields.map(field => ({ [field.field]: field.columnIndex })),
					fileName: csvFile?.name,
				}),
			})
			if (!response.ok) throw new Error(await response.text())
		} catch (error) {
			throw error
		} finally {
			setDialogOpen(false)
		}
	}

	const mutation = useMutation({
		mutationFn: importContactFields,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['csvs'], refetchType: 'active' })
		},
		onError: (error: unknown) => {
			if (error instanceof Error) {
				toast.error('Something went wrong', {
					description: error.message,
				})
			}
		},
	})

	return (
		<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
			<DialogContent className="h-screen sm:max-h-[90vh] px-2 sm:px-6 items-start flex flex-col">
				{mutation.isPending ? (
					<Loader />
				) : (
					<>
						<DialogHeader className="mb-2 flex-shrink-0">
							<DialogTitle>Import from CSV</DialogTitle>
							<DialogDescription>Match up your fields to Hit Em Up</DialogDescription>
							<div className="flex items-center space-x-2">
								<Checkbox
									checked={csvHasHeader}
									onCheckedChange={() => setCsvHasHeader(prevValue => !prevValue)}
									id="csvHasHeader"
								/>
								<label
									htmlFor="csvHasHeader"
									className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
								>
									My file has a title (header) row
								</label>
							</div>
						</DialogHeader>
						<ScrollArea className="flex-1 min-h-0 sm:max-h-[calc(90vh-200px)]">
							{csvFields &&
								previewFields.map((previewField, index) => {
									const setSpecificPreviewField = (columnIndex: number) => {
										const newPreviewedFields = [...previewFields]
										newPreviewedFields[index] = { ...previewField, columnIndex }
										setPreviewFields(newPreviewedFields)
									}
									return (
										<div className="mb-4" key={previewField.field}>
											<CsvImportPreviewItem
												csvFields={csvFields}
												hasHeaderRow={csvHasHeader}
												previewField={previewField}
												setSpecificPreviewField={setSpecificPreviewField}
												tempHeader={tempHeader}
											/>
										</div>
									)
								})}
						</ScrollArea>
						<div className="flex place-content-center flex-shrink-0 mt-4 w-full">
							<Button disabled={!requiredFieldsFilled} onClick={() => mutation.mutate()}>
								Import
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default CsvImportPreview
